import axios, {
  type AxiosResponse,
  type AxiosInstance,
  type AxiosRequestConfig
} from 'axios'
import qs from 'qs'
import Cookies from 'js-cookie'

import router from '@/router'
import { useAuthStore } from '@/stores'
import { COOKIE_KEYS } from '@/constants'

const CONNECTION_TIMEOUT = 30000

abstract class Base {
  protected static DEFAULT_CONFIG: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: CONNECTION_TIMEOUT,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Accept-Language': 'fa',
      'x-device-type': Cookies.get('v') === 'W' ? 'WEB' : 'DESKTOP'
    },
    paramsSerializer: (params) => qs.stringify(params, { indices: false })
  }

  protected static instance: AxiosInstance = axios.create(this.DEFAULT_CONFIG)

  static setToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`
  }

  static checkAuthorization(): void {
    const { purgeAuth } = useAuthStore()
    this.instance.interceptors.response.use(
      function (response) {
        return response
      },
      function (error) {
        if (error.response) {
          const serverErrorCodes = [500, 501, 502, 503, 504, 505]
          if (serverErrorCodes.includes(error.response.status)) {
            router.push({ name: 'ServerError' })
          } else if (error.response.status === 401) {
            purgeAuth(() => {
              router.replace('/auth').then(() => {
                window.history.pushState(
                  null,
                  '',
                  router.currentRoute.value.fullPath
                )
              })
            })
          } else if (
            error.response.status === 402 &&
            router.currentRoute.value.path !== '/buy-plan'
          ) {
            router.replace('/buy-plan').then(() => {
              // Create a new history entry, effectively clearing the previous history
              window.history.pushState(null, '', router.currentRoute.value.fullPath)
            })
            localStorage.setItem(
              'pwa-username',
              JSON.stringify(error.response.data.data.username)
            )
          }
        }
        return Promise.reject(error)
      }
    )
  }

  static removeToken(): void {
    Cookies.remove(COOKIE_KEYS.userToken)
    // biome-ignore lint/complexity/noThisInStatic: <explanation>
    // biome-ignore lint/performance/noDelete: <explanation>
    delete this.instance.defaults.headers.common.Authorization
  }
}

// biome-ignore lint/complexity/noStaticOnlyClass: <explanation>
export abstract class ApiClient extends Base {
  static async get<T = void, Error = void, Data = unknown>(
    url: string,
    config?: AxiosRequestConfig<Data>
  ) {
    return await this.send<T, Error>(url, { ...config, method: 'get' })
  }

  static async post<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return await this.send<T, Error>(url, {
      ...config,
      method: 'post',
      data
    })
  }

  static async postForm<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return await this.send<T, Error>(url, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data' },
      method: 'post',
      data
    })
  }

  static put<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return this.send<T, Error>(url, { ...config, method: 'put', data })
  }

  static putFormData<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return this.send<T, Error>(url, {
      ...config,
      headers: { 'Content-Type': 'multipart/form-data' },
      method: 'put',
      data
    })
  }

  static putVideoStream<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return this.send<T, Error>(url, {
      ...config,
      headers: {
        'Content-Type': 'binary/octet-stream',
        Authorization: undefined,
        Accept: '*/*'
      },
      method: 'put',
      timeout: 900000,
      data,
      baseURL: ''
    })
  }

  static async delete<T = void, Error = void>(
    url: string,
    config?: AxiosRequestConfig
  ) {
    return await this.send<T, Error>(url, { ...config, method: 'delete' })
  }

  static patch<T = void, Error = void, Data = unknown>(
    url: string,
    data?: Data,
    config?: AxiosRequestConfig
  ) {
    return this.send<T, Error>(url, { ...config, method: 'patch', data })
  }

  private static async send<T = void, Error = void>(
    url: string,
    config?: AxiosRequestConfig
  ) {
    const token = Cookies.get(COOKIE_KEYS.userToken)

    if (token)
      ApiClient.instance.defaults.headers.common.Authorization = `Bearer ${token}`

    const conf = { ...this.DEFAULT_CONFIG, ...config }
    const response = await ApiClient.instance.request<T, AxiosResponse<T>, Error>({
      ...conf,
      url
    })
    return response.data
  }
}
